import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ShowroomHero from "../components/ShowroomHero";
import BrandMarquee from "../components/BrandMarquee";
import BrandStrip from "../components/BrandStrip";
import WhatsAppButton from "../components/WhatsAppButton";
import Categories from "../components/Categories";
import CategoryVehicleRow from "../components/CategoryVehicleRow";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import {
  useSeo,
  ORIGIN,
  modelSlug,
  modelPath,
  lp,
  localizedAlternates,
} from "../lib/seo";
import { CATEGORIES, catL10n } from "../lib/categories";
import { PUBLIC_PHONE } from "../lib/contact";

const Testimonials = lazy(() => import("../components/Testimonials"));
const WhyChooseUs = lazy(() => import("../components/WhyChooseUs"));
const HowItWorks = lazy(() => import("../components/HowItWorks"));
const FAQ = lazy(() => import("../components/FAQ"));

const sourcePriority = () => 0;
const HOME_ROW_CAR_LIMIT = 9;

export default function Home() {
  const { t, lang, homeRows } = useApp();
  useSeo({
    title: t.seo.homeTitle,
    description: t.seo.homeDesc,
    keywords: t.seo.homeKeywords,
    canonical: `${ORIGIN}${lp("/")}`,
    alternates: localizedAlternates({ EN: "/eng", TR: "/tr", RU: "/ru" }),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AutoRental",
      "name": "Silver Drive Rent a Car",
      "description": "Ultra-luxury and supercar rental in Dubai — Ferrari, Lamborghini, Rolls-Royce, Bentley and more.",
      "areaServed": "Dubai, United Arab Emirates",
      "url": ORIGIN,
      "telephone": PUBLIC_PHONE,
      "priceRange": "$$$$",
    },
  });
  const [cars, setCars] = useState([]);
  const [showAllModels, setShowAllModels] = useState(false);
  const [showroomWhatsAppVisible, setShowroomWhatsAppVisible] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fleetModels = useMemo(() => Array.from(
    new Map(
      cars
        .filter((car) => car.model)
        .map((car) => [modelSlug(car.brand, car.model), car]),
    ).values(),
  ), [cars]);
  const categoryRows = useMemo(() => CATEGORIES
    .filter((category) => category.showOnHomeFleet !== false)
    .map((category) => {
      const matchingCars = cars
        .filter((car) => category.match(car))
        .sort((first, second) => sourcePriority(first) - sourcePriority(second));
      const selectedIds = homeRows?.[category.key] || [];
      const selectedCars = selectedIds
        .map((id) => matchingCars.find((car) => car.id === id))
        .filter(Boolean)
        .sort((first, second) => sourcePriority(first) - sourcePriority(second));
      const rowCars = selectedIds.length && selectedCars.length
        ? selectedCars
        : matchingCars;

      return {
        category,
        l10n: catL10n(category, lang),
        rowCars: rowCars.slice(0, HOME_ROW_CAR_LIMIT),
      };
    })
    .filter((row) => row.rowCars.length), [cars, homeRows, lang]);

  return (
    <div className="min-h-screen bg-[#F4F1EB]">
      <Header />
      <main>
        <ShowroomHero
          cars={cars}
          onWhatsAppVisibilityChange={setShowroomWhatsAppVisible}
        />
        <BrandMarquee />
        <BrandStrip />
        <Categories />

        <section id="fleet" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 scroll-mt-20">
        <div className="mb-10 max-w-2xl">
          <h2 className="font-cinzel uppercase tracking-widest font-bold text-2xl sm:text-3xl lg:text-4xl text-slate-900">{t.fleet.title}</h2>
          <p className="mt-4 text-slate-600 text-base leading-relaxed">{t.fleet.subtitle}</p>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => <div key={i} className="aspect-[16/10] rounded-2xl bg-black/5 animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-16" data-testid="category-rows">
            {categoryRows.map(({ category, l10n, rowCars }) => (
              <CategoryVehicleRow
                key={category.key}
                category={category}
                l10n={l10n}
                cars={rowCars}
              />
            ))}
          </div>
        )}

        {!loading && fleetModels.length > 0 && (
          <div className="mt-16 pt-10 border-t border-slate-200" data-testid="model-links">
            <h3 className="font-cinzel uppercase tracking-widest font-bold text-lg text-slate-900 mb-5">{t.home.popularModels}</h3>
            <div className={`flex flex-wrap gap-2.5 overflow-hidden transition-[max-height] duration-500 ${showAllModels ? "max-h-[3000px]" : "max-h-[11.5rem]"}`} data-testid="model-links-list">
              {fleetModels.map((c) => (
                <Link key={c.id} to={modelPath(c.brand, c.model)} data-testid={`model-link-${modelSlug(c.brand, c.model)}`}
                  className="px-4 py-2 rounded-full text-sm border border-slate-300 text-slate-700 hover:border-teal-500 hover:text-teal-700 transition-colors duration-200">
                  {lang === "TR" ? `${c.brand} ${c.model} Kirala` : lang === "RU" ? `Аренда ${c.brand} ${c.model}` : `Rent ${c.brand} ${c.model} Dubai`}
                </Link>
              ))}
            </div>
            {fleetModels.length > 24 && (
              <button onClick={() => setShowAllModels((v) => !v)} data-testid="toggle-models" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-900 transition-colors duration-200">
                {showAllModels ? t.home.showLess : t.home.showMore} <span className={`transition-transform duration-300 ${showAllModels ? "rotate-180" : ""}`}>▾</span>
              </button>
            )}
          </div>
        )}
        </section>

        <Suspense fallback={<div className="py-16" />}>
          <Testimonials />
          <WhyChooseUs />
          <HowItWorks />
          <FAQ />
        </Suspense>
      </main>
      <Footer />
      <WhatsAppButton
        hideWhatsAppOnDesktop={false}
        isVisible={!showroomWhatsAppVisible}
      />
    </div>
  );
}
